import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Empty, List, message, Popconfirm, Popover, Skeleton, Switch, Tag } from 'antd';
import { BsTrash } from 'react-icons/bs';
import { LuPencil } from 'react-icons/lu';
import { FiUsers } from 'react-icons/fi';
import { IoMailUnreadOutline } from 'react-icons/io5';
import Box from 'src/components/Box';
import ContactModal from 'src/components/ContactModal';
import { RootState } from 'src/store';
import {
  getContacts,
  removeContact,
  updateContact,
} from 'src/store/contacts/reducer';
import { updateAuthData } from 'src/store/auth/reducer';
import {
  getPendingInvites,
  removePendingInvite,
} from 'src/store/pendingInvites/reducer';
import apiRequests from 'src/utils/api';
import apiRoutes from 'src/utils/apiRoutes';
import asyncErrorHandler from 'src/utils/asyncErrorHandler';
import { UserResponse } from 'src/types';
import InviteModal from '../../components/InviteModal';
import { getCurrentCustomer } from 'src/utils/localStorage';

const Contacts: FC = () => {
  const user: UserResponse = useSelector(
    (globalState: any) => globalState.auth.user
  );
  const project = useSelector(
    (globalState: RootState) => globalState.projectRetainer.data
  );
  const { loading: contactLoading, data: contacts } = useSelector(
    (globalState: RootState) => globalState.contacts
  );
  const { loading: inviteLoading, data: invites } = useSelector(
    (globalState: RootState) => globalState.pendingInvites
  );

  const [updatingStrict, setUpdatingStrict] = useState<string[]>([]);
  const [deletingContacts, setDeletingContacts] = useState<string[]>([]);
  const [updatingInvites, setUpdatingInvites] = useState<string[]>([]);
  const [contactToUpdate, setContactToUpdate] = useState<Record<string, any>>();
  const [inviteModal, setInviteModal] = useState(false);

  const dispatch = useDispatch();

  const onDeleteHandler = async (contact: Record<string, any>) => {
    if (deletingContacts.includes(contact.uuid)) return;

    setDeletingContacts((prev) => [...prev, contact.uuid]);

    const currentCustomer = getCurrentCustomer();

    try {
      await apiRequests.put(
        `${apiRoutes.CONTACTS}/${contact.uuid}`,
        user.projects.length > 1
          ? {
              customers: user.projects
                .map((project: any) => project.customer)
                .filter(
                  (customer: any) => customer.uuid !== currentCustomer.uuid
                )
                .map((customer: any) => customer.uuid),
            }
          : {
              status: 'inactive',
            }
      );

      message.success('Team member removed');
      dispatch(removeContact(contact.uuid));
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setDeletingContacts((prev) => prev.filter((i) => i !== contact.uuid));
    }
  };

  const onCancelInviteHandler = async (uuid: string) => {
    if (updatingInvites.includes(uuid)) return;

    setUpdatingInvites((prev) => [...prev, uuid]);

    try {
      await apiRequests.put(`${apiRoutes.CUSTOMER_INVITE}/${uuid}`, {
        status: 'cancelled',
      });
      dispatch(removePendingInvite(uuid));
      message.success('Invitation canceled');
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setUpdatingInvites((prev) => prev.filter((i) => i !== uuid));
    }
  };

  const onResendInviteHandler = async (uuid: string) => {
    if (updatingInvites.includes(uuid)) return;

    setUpdatingInvites((prev) => [...prev, uuid]);

    try {
      await apiRequests.post(`${apiRoutes.CUSTOMER_INVITE}/${uuid}/resend`);
      message.success('Invitation resent');
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setUpdatingInvites((prev) => prev.filter((i) => i !== uuid));
    }
  };

  const onToggleStrictRequest = async (contact: any, checked: boolean) => {
    if (updatingStrict.includes(contact.uuid)) return;
  
    setUpdatingStrict((prev) => [...prev, contact.uuid]);
  
    try {
      const customerUuid = project?.customer.uuid;
      
      await apiRequests.put(`${apiRoutes.CONTACTS}/${contact.uuid}/customers/${customerUuid}`, {
        strict_requests: checked,
      });
  
      message.success('Strict access updated');
  
      const updatedContact = JSON.parse(JSON.stringify(contact));
      const customer = updatedContact.customers?.find(
        (c: any) => c.uuid === customerUuid
      );
      
      if (customer) {
        customer.pivot.strict_requests = checked;
        dispatch(updateContact(updatedContact));
      }
    } catch (error) {
      asyncErrorHandler(error);
    } finally {
      setUpdatingStrict((prev) => prev.filter((id) => id !== contact.uuid));
    }
  };

  useEffect(() => {
    dispatch(getContacts());
    dispatch(getPendingInvites());
  }, [dispatch]);

  const hasUserStrictRequests = !!project?.customer.strict_requests;

  return (
    <>
      {contactToUpdate && (
        <ContactModal
          contact={contactToUpdate}
          onUpdated={(contact) => {
            dispatch(updateContact(contact));

            if (user.contact.uuid === contact.uuid) {
              dispatch(
                updateAuthData({ name: contact.name, email: contact.email })
              );
            }
          }}
          onClose={() => setContactToUpdate(undefined)}
        />
      )}

      <InviteModal
        visible={inviteModal}
        onInviteSent={() => {
          dispatch(getContacts());
          dispatch(getPendingInvites());
        }}
        onClose={() => setInviteModal(false)}
      />

      <div className="mb-2 flex items-center justify-between px-1 font-bold text-blue-600">
        <div>
          <FiUsers className="mr-2 inline-block text-2xl" />

          <span className="align-middle">Team members</span>
        </div>

        {
          !hasUserStrictRequests ? 
          <Button
            size="small"
            type="primary"
            onClick={() => setInviteModal(true)}
          >
            Invite a team member
          </Button>
          :
          undefined
        }
      </div>

      <Box>
        {contactLoading && !contacts ? (
          <Skeleton className="py-4" />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={contacts?.data}
            locale={{
              emptyText: (
                <Empty
                  description={
                    <span className="text-gray-400">No team members</span>
                  }
                />
              ),
            }}
            renderItem={(item: any) => {
              const customer = item.customers?.find((c: any) => c.uuid === project?.customer.uuid);
              const hasContactStrictRequests = customer?.pivot?.strict_requests ?? false;
              
              const isCurrentUser = item.uuid === user.contact.uuid;

              return (
                <List.Item key={item.uuid} className="flex flex-col w-full">
                  <div className="flex w-full items-center">
                    <span className="basis-2/4">{item.name}</span>
                    <span className="flex-auto mr-4 hidden md:block">{item.email}</span>
            
                    {!hasUserStrictRequests && (
                      <Popover
                        className="popover-strict"
                        content="User can only see requests he is related to"
                        trigger={['hover']}
                      >
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          <Tag
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              marginRight: 18,
                              opacity: isCurrentUser ? 0.6 : 1,
                            }}
                          >
                            <Popconfirm
                              title="Do you want to change strict request access?"
                              disabled={isCurrentUser}
                              onConfirm={() => onToggleStrictRequest(item, !hasContactStrictRequests)}
                            >
                                <Switch
                                  size="small"
                                  checked={hasContactStrictRequests}
                                  loading={updatingStrict.includes(item.uuid)}
                                  disabled={isCurrentUser}
                                />
                            </Popconfirm>
                            <span>Restricted access</span>
                          </Tag>
                          </span>
                      </Popover>
                    )}
            
                    <span className="flex-none space-x-3 text-base">
                      {(!hasUserStrictRequests || isCurrentUser) && (
                        <button onClick={() => setContactToUpdate(item)}>
                          <LuPencil />
                        </button>
                      )}
            
                      {!hasUserStrictRequests && (
                        <Popconfirm
                          title="Delete the team member"
                          onConfirm={() => onDeleteHandler(item)}
                          disabled={isCurrentUser}
                          okText="Yes"
                          cancelText="No"
                        >
                          <button
                            className={
                              isCurrentUser
                                ? 'cursor-not-allowed text-gray-300'
                                : undefined
                            }
                          >
                            <BsTrash />
                          </button>
                        </Popconfirm>
                      )}
                    </span>
                  </div>
                  <span className="block md:hidden w-full text-gray-400">{item.email}</span>
                </List.Item>
              );
            }}
          />
        )}
      </Box>

      {inviteLoading || invites?.data?.length ? (
        <>
          <div className="mb-2 mt-4 px-1 font-bold text-blue-600">
            <IoMailUnreadOutline className="mr-2 inline-block text-2xl" />

            <span className="align-middle">Pending invites</span>
          </div>

          <Box>
            {inviteLoading && (!invites || !invites?.data?.length) ? (
              <Skeleton className="py-4" />
            ) : (
              <List
                itemLayout="horizontal"
                dataSource={invites?.data}
                renderItem={(item: any) => {
                  return (
                    <List.Item key={item.uuid} className="flex w-full items-center">
                      <span className="flex-auto">{item.email}</span>
  
                      {!hasUserStrictRequests && item.strict_requests && (
                        <Popover
                          content="User can only see requests he is related to"
                          motion={{ motionName: '' }}
                          trigger={['hover', 'click']}
                        >
                          <span style={{ display: 'inline-flex' }}>
                            <Tag
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                            >
                              <span>Restricted access</span>
                            </Tag>
                          </span>
                        </Popover>
                      )}
  
                      <span className="flex-none space-x-3">
                        <button
                          className="text-blue-700"
                          disabled={updatingInvites.includes(item.uuid)}
                          onClick={() => onResendInviteHandler(item.uuid)}
                        >
                          Resend
                        </button>
                        <Popconfirm
                          title="Cancel invite"
                          description="Are you sure to cancel the invite to this email?"
                          onConfirm={() => onCancelInviteHandler(item.uuid)}
                          okText="Yes"
                          cancelText="No"
                          disabled={updatingInvites.includes(item.uuid)}
                        >
                          <button className="text-red-500">Cancel</button>
                        </Popconfirm>
                      </span>
                    </List.Item>
                  )
                }}
              />
            )}
          </Box>
        </>
      ) : undefined}
    </>
  );
};

export default Contacts;
